// src/Product/VendorMiniReportModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  RefreshCcw,
  ReceiptText,
  Wallet,
  RotateCcw,
  AlertCircle,
} from "lucide-react";
import { supabase } from "../Services/supabaseClient";
import "../Product/customers.css";

function money(n) {
  const v = Number(n || 0);
  return `₦${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function toDateLabel(d) {
  return d ? String(d) : "—";
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (["paid", "complete", "completed"].includes(s)) return "success";
  if (["partial", "part-paid", "partially_paid"].includes(s)) return "warning";
  if (["unpaid", "open", "overdue"].includes(s)) return "danger";
  return "neutral";
}

function StatCard({ icon: Icon, label, value, hint, tone = "neutral" }) {
  return (
    <div className={`vmr-stat-card ${tone}`}>
      <div className="vmr-stat-icon">
        <Icon size={18} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint && <small>{hint}</small>}
      </div>
    </div>
  );
}

export default function VendorMiniReportModal({
  open,
  onClose,
  companyId,
  vendor,
  fromDate,
  toDate,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);
  const [purchaseRows, setPurchaseRows] = useState([]);
  const [billRows, setBillRows] = useState([]);

  async function load() {
    if (!open) return;
    if (!companyId || !vendor?.vendor_id) return;

    setBusy(true);
    setErr("");

    try {
      const { data: s, error: sErr } = await supabase
        .from("v_vendor_mini_summary")
        .select(
          "vendor_id,name,company_name,currency,billed_total,purchase_paid_total,purchase_refunded_total,bills_total,bills_paid_total,bills_refunded_total,balance"
        )
        .eq("company_id", companyId)
        .eq("vendor_id", vendor.vendor_id)
        .maybeSingle();

      if (!sErr) setSummary(s || null);

      let purQ = supabase
        .from("purchase_transactions")
        .select("id,purchase_date,payment_status,total,created_at")
        .eq("company_id", companyId)
        .eq("vendor_id", vendor.vendor_id)
        .order("purchase_date", { ascending: false })
        .limit(500);

      if (fromDate) purQ = purQ.gte("purchase_date", fromDate);
      if (toDate) purQ = purQ.lte("purchase_date", toDate);

      const { data: pur, error: purErr } = await purQ;
      if (purErr) throw purErr;

      const purIds = (pur || []).map((x) => x.id);

      let setsByPur = {};
      if (purIds.length) {
        const { data: sets, error: setErr } = await supabase
          .from("purchase_settlements")
          .select("id,purchase_id,type,amount,payment_date,notes,created_at")
          .eq("company_id", companyId)
          .in("purchase_id", purIds)
          .order("payment_date", { ascending: false });

        if (setErr) throw setErr;

        (sets || []).forEach((p) => {
          (setsByPur[p.purchase_id] ||= []).push(p);
        });
      }

      const purchaseComputed = (pur || []).map((row) => {
        const sets = setsByPur[row.id] || [];

        const paidFromSets = sets
          .filter((p) => String(p.type || "").toUpperCase() === "PAYMENT")
          .reduce((sum, p) => sum + Number(p.amount || 0), 0);

        const refunded = sets
          .filter((p) => String(p.type || "").toUpperCase() === "REFUND")
          .reduce((sum, p) => sum + Number(p.amount || 0), 0);

        const basePaid =
          String(row.payment_status || "UNPAID").toUpperCase() === "PAID"
            ? Number(row.total || 0)
            : 0;

        const paid = basePaid + paidFromSets;
        const balance = Number(row.total || 0) - (paid - refunded);

        return { ...row, paid, refunded, balance };
      });

      setPurchaseRows(purchaseComputed);

      let bQ = supabase
        .from("bills")
        .select('id,bill_number,date,"due_date",status,total,created_at')
        .eq("company_id", companyId)
        .eq("vendor_id", vendor.vendor_id)
        .order("date", { ascending: false })
        .limit(500);

      if (fromDate) bQ = bQ.gte("date", fromDate);
      if (toDate) bQ = bQ.lte("date", toDate);

      const { data: bills, error: bErr } = await bQ;

      if (!bErr && bills?.length) {
        const billIds = bills.map((x) => x.id);

        let paysByBill = {};
        if (billIds.length) {
          const { data: pays, error: payErr } = await supabase
            .from("bill_payments")
            .select(
              "id,bill_id,payment_date,payment_type,amount,notes,created_at"
            )
            .eq("company_id", companyId)
            .in("bill_id", billIds)
            .order("payment_date", { ascending: false });

          if (payErr) throw payErr;

          (pays || []).forEach((p) => {
            (paysByBill[p.bill_id] ||= []).push(p);
          });
        }

        const billComputed = bills.map((row) => {
          const pays = paysByBill[row.id] || [];

          const paidFromPayments = pays
            .filter(
              (p) => String(p.payment_type || "").toUpperCase() === "PAYMENT"
            )
            .reduce((sum, p) => sum + Number(p.amount || 0), 0);

          const refunded = pays
            .filter(
              (p) => String(p.payment_type || "").toUpperCase() === "REFUND"
            )
            .reduce((sum, p) => sum + Number(p.amount || 0), 0);

          const basePaid =
            String(row.status || "open").toLowerCase() === "paid"
              ? Number(row.total || 0)
              : 0;

          const paid = Math.max(basePaid, paidFromPayments);
          const balance = Number(row.total || 0) - (paid - refunded);

          return { ...row, paid, refunded, balance };
        });

        setBillRows(billComputed);
      } else {
        setBillRows([]);
      }
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to load vendor report.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, vendor?.vendor_id, fromDate, toDate]);

  const purchaseSubtotals = useMemo(() => {
    const list = purchaseRows || [];
    return {
      count: list.length,
      billed: list.reduce((s, r) => s + Number(r.total || 0), 0),
      paid: list.reduce((s, r) => s + Number(r.paid || 0), 0),
      refunded: list.reduce((s, r) => s + Number(r.refunded || 0), 0),
      balance: list.reduce((s, r) => s + Number(r.balance || 0), 0),
    };
  }, [purchaseRows]);

  const billSubtotals = useMemo(() => {
    const list = billRows || [];
    return {
      count: list.length,
      billed: list.reduce((s, r) => s + Number(r.total || 0), 0),
      paid: list.reduce((s, r) => s + Number(r.paid || 0), 0),
      refunded: list.reduce((s, r) => s + Number(r.refunded || 0), 0),
      balance: list.reduce((s, r) => s + Number(r.balance || 0), 0),
    };
  }, [billRows]);

  const grandTotals = useMemo(() => {
    if (summary) {
      const billed =
        Number(summary?.billed_total || 0) + Number(summary?.bills_total || 0);
      const paid =
        Number(summary?.purchase_paid_total || 0) +
        Number(summary?.bills_paid_total || 0);
      const refunded =
        Number(summary?.purchase_refunded_total || 0) +
        Number(summary?.bills_refunded_total || 0);
      const balance = Number(summary?.balance || 0);
      const paymentRate = billed > 0 ? (paid / billed) * 100 : 0;

      return { billed, paid, refunded, balance, paymentRate };
    }

    const billed = purchaseSubtotals.billed + billSubtotals.billed;
    const paid = purchaseSubtotals.paid + billSubtotals.paid;
    const refunded = purchaseSubtotals.refunded + billSubtotals.refunded;
    const balance = purchaseSubtotals.balance + billSubtotals.balance;
    const paymentRate = billed > 0 ? (paid / billed) * 100 : 0;

    return { billed, paid, refunded, balance, paymentRate };
  }, [summary, purchaseSubtotals, billSubtotals]);

  if (!open) return null;

  return (
    <div
      className="vmr-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="vmr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vmr-topbar">
          <div>
            <p className="vmr-eyebrow">Vendor Statement Summary</p>
            <h2>{vendor?.vendor_name || "Unnamed Vendor"}</h2>
            <p>
              {vendor?.company_name ? `${vendor.company_name} • ` : ""}
              Period: <strong>{fromDate || "All dates"}</strong> to{" "}
              <strong>{toDate || "All dates"}</strong>
            </p>
          </div>

          <div className="vmr-actions">
            <button className="vmr-btn ghost" onClick={load} disabled={busy}>
              <RefreshCcw size={16} />
              Refresh
            </button>
            <button className="vmr-icon-btn" onClick={onClose} disabled={busy}>
              <X size={18} />
            </button>
          </div>
        </div>

        {err && (
          <div className="vmr-banner error">
            <AlertCircle size={16} />
            {err}
          </div>
        )}

        <div className="vmr-note">
          This report summarizes purchases, bills, payments, refunds and
          outstanding payables for this vendor.
        </div>

        <div className="vmr-stat-grid">
          <StatCard
            icon={ReceiptText}
            label="Total Purchases"
            value={money(grandTotals.billed)}
            hint={`${purchaseSubtotals.count + billSubtotals.count} record(s)`}
          />
          <StatCard
            icon={Wallet}
            label="Total Paid"
            value={money(grandTotals.paid)}
            hint={`${grandTotals.paymentRate.toFixed(1)}% payment rate`}
            tone="success"
          />
          <StatCard
            icon={RotateCcw}
            label="Refunded"
            value={money(grandTotals.refunded)}
            hint="Refunds received"
            tone="warning"
          />
          <StatCard
            icon={AlertCircle}
            label="Balance Due"
            value={money(grandTotals.balance)}
            hint={
              grandTotals.balance > 0
                ? "Amount still payable"
                : "No outstanding payable"
            }
            tone={grandTotals.balance > 0 ? "danger" : "success"}
          />
        </div>

        {busy && <div className="vmr-loading">Loading vendor report…</div>}

        <div className="vmr-split-summary">
          <div>
            <span>Purchases</span>
            <strong>{purchaseSubtotals.count}</strong>
            <small>{money(purchaseSubtotals.balance)} outstanding</small>
          </div>
          <div>
            <span>Bills</span>
            <strong>{billSubtotals.count}</strong>
            <small>{money(billSubtotals.balance)} outstanding</small>
          </div>
        </div>

        <section className="vmr-section">
          <div className="vmr-section-head">
            <h3>Purchases</h3>
            <p>Purchase transactions recorded against this vendor.</p>
          </div>

          <div className="vmr-table-wrap">
            <table className="vmr-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="num">Total</th>
                  <th className="num">Paid</th>
                  <th className="num">Refunded</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>

              <tbody>
                {purchaseRows.map((r) => (
                  <tr key={r.id}>
                    <td>{toDateLabel(r.purchase_date)}</td>
                    <td>
                      <span
                        className={`vmr-pill ${statusClass(r.payment_status)}`}
                      >
                        {String(r.payment_status || "UNPAID").toUpperCase()}
                      </span>
                    </td>
                    <td className="num">{money(r.total)}</td>
                    <td className="num success-text">{money(r.paid)}</td>
                    <td className="num warning-text">{money(r.refunded)}</td>
                    <td className="num danger-text">
                      <strong>{money(r.balance)}</strong>
                    </td>
                  </tr>
                ))}

                {!!purchaseRows.length && (
                  <tr className="vmr-total-row">
                    <td colSpan="2">Purchases Subtotal</td>
                    <td className="num">{money(purchaseSubtotals.billed)}</td>
                    <td className="num">{money(purchaseSubtotals.paid)}</td>
                    <td className="num">{money(purchaseSubtotals.refunded)}</td>
                    <td className="num">{money(purchaseSubtotals.balance)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {!busy && !purchaseRows.length && (
              <div className="vmr-empty">
                No purchases found for this vendor.
              </div>
            )}
          </div>
        </section>

        <section className="vmr-section">
          <div className="vmr-section-head">
            <h3>Bills</h3>
            <p>
              Vendor bills recorded through the bills workflow, where available.
            </p>
          </div>

          <div className="vmr-table-wrap">
            <table className="vmr-table">
              <thead>
                <tr>
                  <th>Bill #</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th className="num">Total</th>
                  <th className="num">Paid</th>
                  <th className="num">Refunded</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>

              <tbody>
                {billRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.bill_number || "—"}</strong>
                    </td>
                    <td>{toDateLabel(r.date)}</td>
                    <td>{toDateLabel(r.due_date)}</td>
                    <td>
                      <span className={`vmr-pill ${statusClass(r.status)}`}>
                        {String(r.status || "open").toUpperCase()}
                      </span>
                    </td>
                    <td className="num">{money(r.total)}</td>
                    <td className="num success-text">{money(r.paid)}</td>
                    <td className="num warning-text">{money(r.refunded)}</td>
                    <td className="num danger-text">
                      <strong>{money(r.balance)}</strong>
                    </td>
                  </tr>
                ))}

                {!!billRows.length && (
                  <tr className="vmr-total-row">
                    <td colSpan="4">Bills Subtotal</td>
                    <td className="num">{money(billSubtotals.billed)}</td>
                    <td className="num">{money(billSubtotals.paid)}</td>
                    <td className="num">{money(billSubtotals.refunded)}</td>
                    <td className="num">{money(billSubtotals.balance)}</td>
                  </tr>
                )}

                {(purchaseRows.length || billRows.length) && (
                  <tr className="vmr-grand-row">
                    <td colSpan="4">Grand Total</td>
                    <td className="num">{money(grandTotals.billed)}</td>
                    <td className="num">{money(grandTotals.paid)}</td>
                    <td className="num">{money(grandTotals.refunded)}</td>
                    <td className="num">{money(grandTotals.balance)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {!busy && !billRows.length && (
              <div className="vmr-empty">No bills found for this vendor.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
